/* ============================================
   AeroShade 2026 — Runtime
   - Sticky header scroll-state
   - Mobile drawer
   - Reveal-on-scroll
   - Variant picker (color swatches)
   - Quantity selector
   - PDP gallery (mobile carousel)
   ============================================ */

(() => {
  'use strict';

  /* ============================================
     Header scroll state
     ============================================ */
  const header = document.querySelector('[data-aero-header]');
  if (header) {
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ============================================
     Drawers (mobile menu, cart)
     ============================================ */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      const id = opener.getAttribute('data-drawer-open');
      const drawer = document.getElementById(id);
      if (drawer) {
        drawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('.a-drawer');
      if (drawer) {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    }
    if (e.target.classList.contains('a-drawer__overlay')) {
      const drawer = e.target.closest('.a-drawer');
      if (drawer) {
        drawer.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.a-drawer.is-open').forEach((d) => {
        d.classList.remove('is-open');
      });
      document.body.style.overflow = '';
    }
  });

  /* ============================================
     Reveal on scroll
     ============================================ */
  const reveals = document.querySelectorAll('.a-reveal');
  if (reveals.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    reveals.forEach((el) => io.observe(el));
  }

  /* ============================================
     Quantity selector
     ============================================ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty]');
    const input = wrap.querySelector('input[type="number"]');
    const step = parseInt(btn.getAttribute('data-qty-step'), 10);
    const min = parseInt(input.getAttribute('min') || '1', 10);
    const next = Math.max(min, (parseInt(input.value, 10) || min) + step);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ============================================
     Variant picker (radio swatches → form sync)
     ============================================ */
  document.addEventListener('change', (e) => {
    if (!e.target.matches('[data-variant-input]')) return;
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;

    const inputs = form.querySelectorAll('[data-variant-input]:checked');
    const selected = Array.from(inputs).map((i) => i.value);
    const productHandle = form.getAttribute('data-product-handle');

    fetch(`/products/${productHandle}.js`)
      .then((r) => r.json())
      .then((p) => {
        const variant = p.variants.find((v) =>
          selected.every((val, i) => v[`option${i + 1}`] === val)
        );
        if (!variant) return;
        const variantInput = form.querySelector('[name="id"]');
        if (variantInput) variantInput.value = variant.id;

        const priceEl = form.querySelector('[data-variant-price]');
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        const wasEl = form.querySelector('[data-variant-compare-price]');
        if (wasEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            wasEl.textContent = formatMoney(variant.compare_at_price);
            wasEl.style.display = '';
          } else {
            wasEl.style.display = 'none';
          }
        }

        const atc = form.querySelector('[data-atc-btn]');
        if (atc) {
          if (variant.available) {
            atc.removeAttribute('disabled');
            atc.textContent = 'ADD TO BAG';
          } else {
            atc.setAttribute('disabled', '');
            atc.textContent = 'SOLD OUT';
          }
        }

        const labelEl = form.querySelector(`[data-option-display="${e.target.name}"]`);
        if (labelEl) labelEl.textContent = e.target.value;

        // Swap gallery image to variant's featured_media — match by ID (reliable)
        const gallery = document.querySelector('[data-pdp-gallery]');
        if (gallery && variant.featured_media && variant.featured_media.id) {
          const targetId = String(variant.featured_media.id);
          const matchedTile = gallery.querySelector(`[data-media-id="${targetId}"]`);
          if (matchedTile && matchedTile !== gallery.firstElementChild) {
            gallery.insertBefore(matchedTile, gallery.firstElementChild);
          }
        } else if (gallery && variant.featured_image && variant.featured_image.id) {
          const targetId = String(variant.featured_image.id);
          const matchedTile = gallery.querySelector(`[data-media-id="${targetId}"]`);
          if (matchedTile && matchedTile !== gallery.firstElementChild) {
            gallery.insertBefore(matchedTile, gallery.firstElementChild);
          }
        }

        // Absolutely NO scroll behavior
      });
  });

  function formatMoney(cents) {
    const n = (cents / 100).toFixed(2);
    return `£${n}`;
  }

  /* ============================================
     Add to cart (AJAX)
     ============================================ */
  document.addEventListener('submit', async (e) => {
    if (!e.target.matches('form[data-product-form]')) return;
    e.preventDefault();
    const form = e.target;
    const atc = form.querySelector('[data-atc-btn]');
    const original = atc ? atc.textContent : '';
    if (atc) {
      atc.setAttribute('disabled', '');
      atc.textContent = 'ADDING...';
    }

    const fd = new FormData(form);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        headers: { 'Accept': 'application/json' }
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || 'Could not add to cart');

      // Update cart count
      const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await cartRes.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });

      // Open cart drawer if it exists
      const drawer = document.getElementById('aero-cart-drawer');
      if (drawer) {
        drawer.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      } else {
        if (atc) atc.textContent = 'ADDED ✓';
        setTimeout(() => { if (atc) atc.textContent = original; }, 1600);
      }
    } catch (err) {
      console.error(err);
      if (atc) atc.textContent = 'TRY AGAIN';
      setTimeout(() => { if (atc) atc.textContent = original; }, 1600);
    } finally {
      if (atc) atc.removeAttribute('disabled');
    }
  });

  /* ============================================
     Newsletter (basic AJAX submit)
     ============================================ */
  document.addEventListener('submit', async (e) => {
    if (!e.target.matches('[data-newsletter-form]')) return;
    e.preventDefault();
    const form = e.target;
    const btn = form.querySelector('button');
    const original = btn ? btn.textContent : '';
    if (btn) btn.textContent = 'SENDING...';
    const fd = new FormData(form);
    try {
      await fetch(form.action, { method: 'POST', body: fd });
      if (btn) btn.textContent = 'THANKS ✓';
      form.reset();
    } catch (err) {
      if (btn) btn.textContent = 'TRY AGAIN';
    } finally {
      if (btn) setTimeout(() => { btn.textContent = original; }, 2400);
    }
  });
})();
